(ns app.survey.form.sub
    (:require
     [clojure.edn :as edn]
     [clojure.string :as string]
     [common.currencies :as currencies]
     [common.languages :as languages]
     [common.localization :as loc]
     [common.util :as cu]
     [frontend.logging :as logging]
     [frontend.view.nev.surveys.form.sub :as nev-sub]
     [re-frame.core :as rf]
     [ui.editors.data-input :as data-input]))

(rf/reg-sub
  ::db
  (fn [db]
    db))

(rf/reg-sub
  ::language
  (fn [db]
    (get db ::language :ENG)))

(rf/reg-sub
  ::lang-options
  (fn [db]
    (get-in db
            [::lang-options]
            [{:value :ENG
              :label (languages/get-label :ENG)}])))

(rf/reg-sub
  ::template
  (fn [db]
    (get-in db [::template])))

(rf/reg-sub
  ::answers
  (fn [db]
    (get-in db [::answers])))

(rf/reg-sub
  ::survey-no-answer-descriptions
  (fn [db]
    (get-in db [::survey-no-answer-descriptions])))

(rf/reg-sub
  ::survey-no-answer-categories
  (fn [db]
    (get-in db [::survey-no-answer-categories])))

(rf/reg-sub
  ::previous-answers
  (fn [db]
    (get-in db [::previous-answers])))

(rf/reg-sub
  ::questions
  (fn [db]
    (get-in db [::questions])))

(rf/reg-sub
  ::questions-ext-ids
  (fn [db]
    (get-in db [::questions-ext-ids])))

(rf/reg-sub
  ::question-options
  (fn [db]
    (get-in db [::question-options])))

(rf/reg-sub
  ::survey-process
  (fn [db]
    (get-in db [::survey-process])))

(rf/reg-sub
  ::burned-config
  :<-
  [::survey-process]
  :<-
  [::previous-answers]
  (fn [[survey-process previous-answers] _]
    (when-let [burned-config (:burned-config survey-process)]
      (assoc burned-config
             :db/id (:db/id survey-process)
             :selected-lang (:language survey-process)
             :previous-answers previous-answers))))

(rf/reg-sub
  ::survey-id
  :<-
  [::survey-process]
  (fn [process _]
    (get process :db/id)))

(rf/reg-sub
  ::process-status
  :<-
  [::survey-process]
  (fn [process _]
    (:status process)))

(rf/reg-sub
  ::read-only?
  :<-
  [::process-status]
  (fn [status]
    (not= status :survey-process.status/wip)))

(defn resolve-block-info
  [{:keys [lang block question-lookup focused-question-id]}]
  (or (->> block
           :content
           (keep (fn [item]
                   (when (and (not (cu/blank-value? focused-question-id))
                              (= focused-question-id
                                 (or (:survey-question-id item)
                                     (:id item)
                                     (:identifier item))))
                     (let [template-question-info (get-in item [:survey-question/info-translations lang])
                           question-info (get-in (question-lookup focused-question-id) [:survey-question/info-translations lang])]
                       (or (when-not (cu/blank-value? template-question-info)
                             template-question-info)
                           (when-not (cu/blank-value? question-info)
                             question-info))))))
           (first))
      (get-in block [:info lang])))

(defn resolve-value
  [{:keys [no-answer-descriptions-lookup edit-answer-lookup] :as props} {:keys [calculi-type rules] :as rule}]
  (case calculi-type
    :calculi-type/operator-sum
    (let [sum-value (->> rules
                         (map (partial resolve-value props))
                         (remove nil?)
                         (apply +))]
      sum-value)
    :calculi-type/operator-subtraction
    (let [subs-value (->> rules
                          (map (partial resolve-value props))
                          (remove nil?)
                          (apply -))]
      subs-value)
    :calculi-type/operator-division
    (let [div-value (->> rules
                         (map (partial resolve-value props))
                         (remove nil?)
                         (apply /))]
      div-value)
    :calculi-type/operator-multiplication
    (let [mult-value (->> rules
                          (map (partial resolve-value props))
                          (remove nil?)
                          (apply *))]
      mult-value)
    :calculi-type/parenthesis-group
    (let [groupv-val (->> rules
                          (map (partial resolve-value props))
                          (first))]
      groupv-val)
    :calculi-type/constant
    (:value rule)

    :calculi-type/field
    (let [fld (:field rule)
          value (get edit-answer-lookup fld)
          no-answer? (get no-answer-descriptions-lookup fld)
          res (cond
                no-answer?
                nil
                (string? value)
                (edn/read-string value)
                (js/isNaN value)
                nil
                (number? value)
                value
                :else
                nil)]
      res)
    (do (js/console.log "Unknown calculi type : " calculi-type)
        nil)))

(defn resolve-calculated-value
  [props question]
  (->> question
       :survey-question/calculation-rules
       (map (partial resolve-value props))
       (first)))

(defn visibility-value
  [{:keys [edit-answer-lookup] :as props} rules rule]
  (case (:query-type rule)
    :query-type/and-collection
    (->> rule
         :rules
         (every? (partial visibility-value props rules)))
    :query-type/or-collection
    (->> rule
         :rules
         (some (partial visibility-value props rules)))
    :query-type/single-item
    (let [fld (:field rule)
          fld-val (get edit-answer-lookup fld)]
      (case (:operator rule)
        :operand/known (not (cu/blank-value? fld-val))
        :operand/unknown (cu/blank-value? fld-val)
        :operand/any-of (some (set (:value rule))
                              (cond (string? fld-val)
                                    [fld-val]
                                    (coll? fld-val)
                                    fld-val
                                    :else
                                    [fld-val]))
        :operand/all-of (every? (set (:value rule))
                                (cond (string? fld-val)
                                      [fld-val]
                                      (coll? fld-val)
                                      fld-val
                                      :else
                                      [fld-val]))
        :operand/none-of (not-any? (set (:value rule))
                                   (cond (string? fld-val)
                                         [fld-val]
                                         (coll? fld-val)
                                         fld-val
                                         :else
                                         [fld-val]))
        :operand/greater-than (> fld-val (:value rule))
        :operand/smaller-than (< fld-val (:value rule))
        :operand/starts-with (string/starts-with? (cu/str-trim-lcase (:value rule)) (cu/str-trim-lcase fld-val))
        :operand/after (> fld-val (:value rule))
        :operand/before (< fld-val (:value rule))
        :operand/contains-exactly (= (cu/str-trim-lcase (:value rule)) (cu/str-trim-lcase fld-val))
        :operand/equals (= (cu/str-trim-lcase (:value rule)) (cu/str-trim-lcase fld-val))
        :operand/equal-or-greater-than (>= fld-val (:value rule))
        :operand/equal-or-smaller-than (<= fld-val (:value rule))
        (do (logging/unimplemented "Unknown visibility operator " (:operator rule)
                                   "\n" rule
                                   "\n" fld-val
                                   "\n" (:value rule))
            true)))))

(defn calc-visibility
  [{:keys [item] :as props}]
  (case (:visibility item)
    nil true
    :visibility/always true
    :visibility/hidden false
    :visibility/conditional
    (->> item
         :visibility-rules
         (map (partial visibility-value props (:visibility-rules item)))
         (first)
         (boolean))))

(defn calc-mandatoriness
  [_props]
  ;; TODO: why is this empty?
  )

(defn resolve-question-data
  [{:keys [lang question-lookup questions-ext-ids item edit-answer-lookup previous-answers no-answer-descriptions-lookup]
    :as   props}]
  (let [{:keys [survey-question-id visibility]} item
        question (get question-lookup survey-question-id)
        question-ext-ids (->> (:survey-question/external-ids question)
                              (mapv (fn [ext-id-ref]
                                      (->> questions-ext-ids
                                           (filter #(= (:db/id %) (:db/id ext-id-ref)))
                                           (first)))))
        data-type (:survey-question/data-type question)
        question-label (get-in question [:survey-question/label-translations lang])
        previous-answer-exists? (contains? previous-answers survey-question-id)
        previous-value-raw (get previous-answers survey-question-id)
        options (if (= (:survey-question/data-type question)
                       :data.type/currency-symbol)
                  currencies/value-options
                  (->> question
                       :survey-question/options
                       (sort-by :common/ord)
                       (mapv (fn [o]
                               {:value (:db/id o)
                                :label (or (get-in o [:survey-question-option/label-translations lang])
                                           (->> o
                                                :survey-question-option/label-translations
                                                (first)
                                                (val)))}))))
        previous-value (when (and previous-answer-exists?
                                  (not (nil? previous-value-raw))
                                  (not (and (string? previous-value-raw)
                                            (cu/blank-value? previous-value-raw))))
                         (data-input/formatted-display-string
                           {:data-type   data-type
                            :value       previous-value-raw
                            :options     options
                            :lang        lang
                            :date-format (loc/tr :date-format)}))
        resolved-item (case (:survey-item-type item)
                        :survey-item/wysiwyg
                        (assoc item :rich-content (get-in item [:content lang]))
                        :survey-item/question
                        (merge question
                               item
                               {:options        options
                                :external-ids   question-ext-ids
                                :no-answer?     (get no-answer-descriptions-lookup survey-question-id)
                                :hidden?        (or (= visibility :visibility/hidden)
                                                    (and (= visibility :visibility/conditional)
                                                         false))
                                :data-unit      (:survey-question/data-unit question)
                                :data-type      (if (= :data.type/calculated-number data-type)
                                                  :data.type/number
                                                  data-type)
                                :calculated?    (= :data.type/calculated-number data-type)
                                :label          question-label
                                :previous-value (when (not= data-type :data.type/calculated-number)
                                                  previous-value)
                                :value          (if (not= :data.type/calculated-number data-type)
                                                  (get edit-answer-lookup survey-question-id)
                                                  (let [calc-val (resolve-calculated-value props question)]
                                                    (if (js/isNaN calc-val)
                                                      nil
                                                      calc-val)))})
                        (logging/unimplemented "Unknown survey-item-type-" item))
        wysiwyg-item? (= :survey-item/wysiwyg (:survey-item-type item))
        visible? (calc-visibility props)
        mandatory? (or (= :mandatory/always (:mandatoriness item))
                       (and visible?
                            (= :mandatory/when-visible (:mandatoriness item)))
                       (and (some #{(:mandatoriness item)} [:mandatory/conditional :mandatory/conditional-when-visible])
                            (calc-mandatoriness props)))]
    (js/console.log "Mandatory : " mandatory? " " visible? " <<< " item)
    (assoc resolved-item
           :visible? (and visible?
                          (or wysiwyg-item?
                              (not (cu/blank-value? question-label))))
           :mandatory? mandatory?)))

(defn resolve-config
  [db]
  (let [lang (get db ::language :ENG)
        template (get db ::template)
        saved-answers (get db ::answers)
        previous-answers (get db ::previous-answers)
        edited-answers (get db ::nev-sub/edit-answer-lookup)
        survey-no-answer-descriptions (get db ::survey-no-answer-descriptions)
        edited-no-answer-lookup (get db ::nev-sub/edit-no-answer-lookup)
        combined-no-answer-lookup (merge survey-no-answer-descriptions edited-no-answer-lookup)

        question-lookup (get db ::questions)
        questions-ext-ids (get db ::questions-ext-ids)
        focused-question-id (get db ::nev-sub/focused-question-id)
        process (get db ::survey-process)
        status (:status process)
        read-only? (not= status :survey-process.status/wip)
        template-config (:survey-template/config template)
        combined-answer-lookup (merge saved-answers edited-answers)
        props {:lang                          lang
               :edit-answer-lookup            combined-answer-lookup
               :no-answer-descriptions-lookup combined-no-answer-lookup
               :previous-answers              previous-answers
               :question-lookup               question-lookup
               :questions-ext-ids             questions-ext-ids
               :focused-question-id           focused-question-id}]
    (->> template-config
         :blocks
         (map-indexed
           (fn [block-index block]
             (case (:block-type block)
               :survey-block/wysiwyg
               (assoc block :wysiwyg-text (get-in block [:content lang]))
               :survey-block/section
               (-> block
                   (update-in [:label]
                              (fn [lbl]
                                (get lbl lang)))
                   (assoc :read-only? read-only?)
                   (assoc :block-info
                          (resolve-block-info
                            (assoc props :block block)))
                   (update-in [:content]
                              (fn [content]
                                (->> content
                                     (map-indexed (fn [section-index item]
                                                    (resolve-question-data
                                                      (assoc props
                                                             :question-lookup question-lookup
                                                             :section-index section-index
                                                             :item item
                                                             :block-index block-index))))
                                     (filter :visible?)
                                     (vec)))))
               :survey-block/table-block
               (-> block
                   (update-in [:block] (fn [lbl]
                                         (get lbl lang)))
                   (assoc :read-only? read-only?)
                   (assoc :block-info
                          (resolve-block-info
                            (assoc props :block block)))
                   (update-in [:content]
                              (fn [content]
                                (->> content
                                     (map-indexed (fn [section-index item]
                                                    (resolve-question-data
                                                      (assoc props
                                                             :question-lookup question-lookup
                                                             :section-index section-index
                                                             :item item
                                                             :block-index block-index))))
                                     (filter :visible?)
                                     (vec)))))
               (do (logging/unimplemented "Uknknown survey block " block)
                   block))))
         (vec))))

(rf/reg-sub
  ::config
  (fn [db _]
    (resolve-config db)))

(rf/reg-sub
  ::unsaved-changes
  :<-
  [::nev-sub/edit-answer-lookup]
  :<-
  [::answers]
  :<-
  [::nev-sub/edit-no-answer-lookup]
  :<-
  [::survey-no-answer-descriptions]
  (fn [[unsaved-changes answers unsaved-no-answers survey-no-answer-descriptions]]
    (let [res (seq (concat (->> unsaved-changes
                                (remove (fn [[q v]]
                                          (let [old-answer (get answers q)]
                                            (or (and (cu/blank-value? v)
                                                     (cu/blank-value? old-answer))
                                                (= v old-answer)
                                                (= (pr-str v) old-answer)))))
                                (into {})
                                (seq))
                           (->> unsaved-no-answers
                                (remove (fn [[id v]]
                                          (= v (get survey-no-answer-descriptions id))))
                                (seq))))]
      res)))

(rf/reg-sub
  ::missing-fields
  :<-
  [::config]
  (fn [config]
    (->> config
         (mapcat (fn [{:keys [block-type content]}]
                   (when (= block-type :survey-block/section)
                     (->> content
                          (keep (fn [{:keys [mandatory? hidden? value label no-answer? calculated?]}]
                                  (let [missing-label (when (and mandatory?
                                                                 (not no-answer?)
                                                                 (not calculated?)
                                                                 (not hidden?)
                                                                 (cu/blank-value? value))
                                                        label)]
                                    missing-label)))
                          (seq)))))
         (distinct)
         (seq))))
